import { useCallback, useContext, useState } from 'react';
import GlobalContext from '@/context/GlobalContext';
import { supabase } from '@/lib/supabase';
import type { CourseWithFullDetails } from '@/lib/types';
import CourseEditContext from '@/Pages/Courses/CourseEditContext';

export type AiRewriteFeature = 'improve_writing' | 'align_blooms';

interface UseAiRewriteReturn {
	rewrite: (feature: AiRewriteFeature, content: string) => Promise<void>;
	loading: boolean;
	activeFeature: AiRewriteFeature | null;
	result: string | null;
	logId: string | null;
	markAccepted: () => void;
	clear: () => void;
	error: string | null;
}

const useAiRewrite = (): UseAiRewriteReturn => {
	const { currentUser } = useContext(GlobalContext);
	const { courseData } = useContext(CourseEditContext) as unknown as {
		courseData: CourseWithFullDetails;
	};

	const [loading, setLoading] = useState(false);
	const [activeFeature, setActiveFeature] = useState<AiRewriteFeature | null>(null);
	const [result, setResult] = useState<string | null>(null);
	const [logId, setLogId] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);

	const rewrite = useCallback(
		async (feature: AiRewriteFeature, content: string) => {
			if (!currentUser?.user_id || !currentUser?.school_id) return;

			setLoading(true);
			setActiveFeature(feature);
			setResult(null);
			setError(null);

			try {
				const { data, error: fnError } = await supabase.functions.invoke('ai-rewrite', {
					body: {
						feature,
						content,
						courseId: courseData?.course_id ?? undefined,
						schoolId: currentUser.school_id,
						userId: currentUser.user_id,
					},
				});

				if (fnError) throw fnError;

				setResult(data?.result ?? null);
				setLogId(data?.logId ?? null);
			} catch (err) {
				console.error('useAiRewrite error:', err);
				setError('Could not rewrite content. Please try again.');
			} finally {
				setLoading(false);
			}
		},
		[courseData, currentUser]
	);

	const markAccepted = useCallback(() => {
		if (!logId) return;
		supabase.functions.invoke('ai-mark-accepted', { body: { logId } }).then(({ error: err }) => {
			if (err) console.error('Failed to mark AI rewrite log as accepted:', err);
		});
	}, [logId]);

	const clear = useCallback(() => {
		setResult(null);
		setLogId(null);
		setError(null);
		setActiveFeature(null);
	}, []);

	return { rewrite, loading, activeFeature, result, logId, markAccepted, clear, error };
};

export default useAiRewrite;
